import React, { useState, useEffect } from 'react';
import api from '../api';
import VoiceSearchButton from '../components/VoiceSearchButton';

var SCORE_COLORS = {
  Excellent: { bg: '#dcfce7', text: '#16a34a' },
  Good: { bg: '#dbeafe', text: '#2563eb' },
  Average: { bg: '#fef3c7', text: '#b45309' },
  Risky: { bg: '#fee2e2', text: '#dc2626' },
};

export default function ClientScore() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'recovery_pct', dir: 'asc' });

  useEffect(() => {
    api.get('/client-score').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const fmt = (n) => '\u20B9' + Math.round(n || 0).toLocaleString('en-IN');

  function handleSort(key) {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  }
  function sortIndicator(key) {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  const filtered = data.filter(d => !search.trim() || d.name.toLowerCase().includes(search.trim().toLowerCase()) || String(d.client_number).includes(search.trim()));
  const sorted = filtered.slice().sort((a, b) => {
    let av = a[sort.key], bv = b[sort.key];
    if (typeof av === 'string') { av = (av || '').toLowerCase(); bv = (bv || '').toLowerCase(); }
    else { av = av == null ? -1 : av; bv = bv == null ? -1 : bv; }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const counts = { Excellent: 0, Good: 0, Average: 0, Risky: 0 };
  data.forEach(d => { counts[d.score]++; });

  const columns = [
    { key: 'name', label: 'Client' },
    { key: 'loan_count', label: 'Loans' },
    { key: 'first_loan_date', label: 'Since' },
    { key: 'total_lent', label: 'Total Lent' },
    { key: 'total_income', label: 'Total Income' },
    { key: 'recovery_pct', label: 'Recovery %' },
    { key: 'timely_pct', label: 'Timely %' },
    { key: 'score', label: 'Score' },
  ];

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 8 }}>Client Score</h2>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        Recovery % = (interest deducted + interest collected) \u00F7 total amount ever lent to that client, across all their loans.
        Timely % = of their fully-paid installments, how many were paid within 2 days of the due date. Score is based on Recovery %: Excellent \u2265 70%, Good \u2265 50%, Average \u2265 30%, Risky &lt; 30%.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {['Excellent', 'Good', 'Average', 'Risky'].map(s => (
          <div key={s} style={{ background: SCORE_COLORS[s].bg, borderRadius: 8, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: SCORE_COLORS[s].text }}>{counts[s]}</div>
            <div style={{ fontSize: 13, color: SCORE_COLORS[s].text }}>{s}</div>
          </div>
        ))}
      </div>

      <div style={{ maxWidth: 320, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or client #..." style={{ flex: 1, padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }} />
          <VoiceSearchButton onResult={(transcript) => setSearch(transcript)} />
        </div>
        {search && <button type="button" onClick={() => setSearch('')} style={{ marginTop: 6, padding: 0, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13 }}>✕ Clear search</button>}
      </div>

      <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {columns.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} style={{ padding: '10px 16px', textAlign: col.key === 'name' ? 'left' : 'right', fontSize: 12, fontWeight: 500, color: sort.key === col.key ? '#2563eb' : '#6b7280', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>{col.label}{sortIndicator(col.key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(d => (
              <tr key={d.customer_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ fontWeight: 500 }}>{d.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>#{d.client_number} {d.area ? '| ' + d.area : ''}</div>
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>{d.loan_count} {d.active_loan_count > 0 ? <span style={{ color: '#16a34a', fontSize: 11 }}>({d.active_loan_count} active)</span> : null}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13 }}>{d.first_loan_date ? new Date(d.first_loan_date).toLocaleDateString('en-IN') : '-'}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>{fmt(d.total_lent)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#16a34a' }}>{fmt(d.total_income)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>{d.recovery_pct}%</td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>{d.timely_pct === null ? '-' : d.timely_pct + '%'}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  <span style={{ background: SCORE_COLORS[d.score].bg, color: SCORE_COLORS[d.score].text, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{d.score}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sorted.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No clients with loans yet</div>}
      </div>
    </div>
  );
}
